import React from 'react';
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import { CircleAlert, Download, Eye } from 'lucide-react';
import type { BatchStatus, SavingPaymentBatch } from './types';

interface SavingsBatchTableProps {
  batches: SavingPaymentBatch[];
  isLoading: boolean;
  onDownload: (batch: SavingPaymentBatch) => void;
  onViewItems: (batch: SavingPaymentBatch) => void;
}

// Payment status pill, distinct from recon progress. RECON_SENT means
// reconciled but unpaid ("ready to pay"); PAYMENT_COMPLETE is fully paid;
// PAYMENT_PARTIAL is paid bar lines with no provider account (see tooltip).
const statusTag = (
  status: BatchStatus
): {
  variant: React.ComponentProps<typeof Tag>['variant'];
  label: string;
  hint?: string;
} => {
  switch (status) {
    case 'PAYMENT_COMPLETE':
      return { variant: 'success', label: 'Paid' };
    case 'PAYMENT_PARTIAL':
      return {
        variant: 'warning',
        label: 'Partially paid',
        hint: 'Some lines had no provider account and were skipped; they are re-attempted in the next period.',
      };
    case 'RECON_SENT':
      return { variant: 'outline-navy', label: 'Ready to pay' };
    case 'FAILED':
      return { variant: 'failed', label: 'Failed' };
    case 'RECON_PENDING':
    default:
      return { variant: 'pending', label: 'Reconciling' };
  }
};

const formatPeriod = (period: string): string => {
  const [year, month] = period.split('-');
  if (!year || !month) return period;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('en-ZA', { month: 'short', year: 'numeric' });
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const formatCurrency = (amount: string): string => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `R${n.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const SavingsBatchTable = ({
  batches,
  isLoading,
  onDownload,
  onViewItems,
}: SavingsBatchTableProps) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Batch</TableHead>
            <TableHead>Employer</TableHead>
            <TableHead>Pay Cycle</TableHead>
            <TableHead>Period</TableHead>
            <TableHead>Generated</TableHead>
            <TableHead className="text-right">No. Employees</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Provider Ref</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {batches.map((batch) => {
            const tag = statusTag(batch.status);
            const statusHint =
              batch.status === 'FAILED' && batch.failureReason
                ? batch.failureReason
                : tag.hint;
            return (
              <TableRow key={batch.id}>
                <TableCell className="font-medium">#{batch.id}</TableCell>
                <TableCell>{batch.employerName}</TableCell>
                <TableCell className="capitalize">
                  {batch.payCycleFrequency}
                </TableCell>
                <TableCell>{formatPeriod(batch.period)}</TableCell>
                <TableCell>{formatDate(batch.generatedAt)}</TableCell>
                <TableCell className="text-right">
                  {batch.saversCount}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(batch.totalAmount)}
                  {batch.invalidCount > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="block cursor-help text-xs font-normal text-greyscale-text-caption">
                          +{batch.invalidCount} invalid
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {batch.invalidCount} line
                        {batch.invalidCount === 1 ? '' : 's'} (
                        {formatCurrency(batch.invalidAmount)}) skipped or with
                        no Franc account — excluded from the payable total.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Tag variant={tag.variant}>{tag.label}</Tag>
                    {statusHint && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CircleAlert className="size-4 cursor-help text-[--greyscale-text-caption]" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {statusHint}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {batch.providerReference ?? '—'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onViewItems(batch)}
                          aria-label="View items">
                          <Eye className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>View items</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onDownload(batch)}
                          aria-label="Download batch">
                          <Download className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Download</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};

export default SavingsBatchTable;
